import {AfterViewInit, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, Renderer2, SimpleChanges} from '@angular/core';

@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appTooltip') tooltipText!: string;
  @Input() tooltipPlacement: 'top' | 'bottom' | 'start' | 'end' = 'top';
  @Input() tooltipColor: 'primary' | 'secondary' | 'light' | 'dark'  = 'secondary';

  private tooltipElement!: HTMLElement;
  private tooltipInner!: HTMLElement;
  private tooltipArrow!: HTMLElement;
  /** Set while the tooltip is visible: removes the scroll and resize listeners that keep it with its host. */
  private stopFollowing: (() => void) | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2, private zone: NgZone) {
  }

  /**
   * Follow the text after the tooltip exists. It was written once, at init, so a bound value that
   * arrives later (e.g. `{{ file | estimatedRestorationTime | async }}`, which starts at
   * "Calculating...") never showed (VTK-3469).
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltipText'] && this.tooltipInner) {
      this.renderer.setProperty(this.tooltipInner, 'textContent', this.tooltipText);
    }
  }

  ngAfterViewInit(): void {
    this.createTooltip();
    const host = this.el.nativeElement;
    // Describe the host by the tooltip, so a screen reader announces the text without a mouse. Appended
    // to any description the host already has.
    const described = [host.getAttribute('aria-describedby'), this.tooltipElement.id].filter(Boolean).join(' ');
    this.renderer.setAttribute(host, 'aria-describedby', described);

    this.renderer.listen(host, 'mouseenter', () => this.showTooltip());
    this.renderer.listen(host, 'mouseleave', () => {
      if (!host.matches(':focus-visible')) {
        this.hideTooltip();
      }
    });
    // Keyboard: a focusable host (a link, or an element given a tabindex) shows it on focus. Escape
    // dismisses it without moving focus (WCAG 1.4.13).
    this.renderer.listen(host, 'focus', () => {
      if (host.matches(':focus-visible')) {
        this.showTooltip();
      }
    });
    this.renderer.listen(host, 'blur', () => {
      if (!host.matches(':hover')) {
        this.hideTooltip();
      }
    });
    this.renderer.listen(host, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.hideTooltip();
      }
    });
  }

  private static nextId = 0;

  private createTooltip(): void {
    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.setAttribute(this.tooltipElement, 'id', `app-tooltip-${TooltipDirective.nextId++}`);
    this.renderer.setAttribute(this.tooltipElement, 'role', 'tooltip');
    this.renderer.addClass(this.tooltipElement, 'tooltip');
    this.renderer.addClass(this.tooltipElement, `bs-tooltip-${this.tooltipPlacement}`);
    this.renderer.addClass(this.tooltipElement, `cl-tooltip`);
    this.renderer.addClass(this.tooltipElement, `cl-tooltip-${this.tooltipColor}`);
    // Fixed, not absolute: showTooltip() places it with viewport coordinates (getBoundingClientRect), which
    // only line up with a position that is also relative to the viewport. Absolute drifted by the page's
    // scroll offset.
    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');

    this.tooltipInner = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipInner, 'tooltip-inner');
    this.renderer.setStyle(this.tooltipInner, 'white-space', 'pre-line');
    this.renderer.setProperty(this.tooltipInner, 'textContent', this.tooltipText);

    this.tooltipArrow = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipArrow, 'tooltip-arrow');
    this.renderer.setStyle(this.tooltipArrow, 'position', 'absolute');

    this.renderer.appendChild(this.tooltipElement, this.tooltipArrow);
    this.renderer.appendChild(this.tooltipElement, this.tooltipInner);
    this.renderer.appendChild(document.body, this.tooltipElement);
  }

  private showTooltip(): void {
    this.positionTooltip();
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '1');
    this.followHostWhileVisible();
  }

  /**
   * The tooltip is fixed, so it keeps its viewport position when the page or a scrollable ancestor of the
   * host scrolls (or the window resizes), leaving it over whatever has moved under it. That is easy to
   * reach from the keyboard: a focused host does not blur when scrolled, and focus can also scroll the
   * host into view after the tooltip was placed (the library smooth-scrolls). So re-place it with the host
   * for as long as it is visible. Scroll does not bubble, hence the capture listener on the document, which
   * also sees every scrollable ancestor.
   */
  private followHostWhileVisible(): void {
    if (this.stopFollowing) {
      return;
    }
    const reposition = () => this.positionTooltip();
    // Outside the zone: re-placing a tooltip is style-only and must not trigger change detection per scroll event.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('scroll', reposition, {capture: true, passive: true});
      window.addEventListener('resize', reposition, {passive: true});
    });
    this.stopFollowing = () => {
      document.removeEventListener('scroll', reposition, {capture: true});
      window.removeEventListener('resize', reposition);
      this.stopFollowing = null;
    };
  }

  private positionTooltip(): void {
    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.tooltipElement.getBoundingClientRect();

    let top = 0, left = 0, arrowTop = '', arrowLeft = '';
    switch (this.tooltipPlacement) {
      case 'top':
        top = hostPos.top - tooltipPos.height - 5;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        arrowTop = 'calc(100% + 4px)';
        arrowLeft = '50%';
        break;
      case 'bottom':
        top = hostPos.bottom + 5;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        arrowTop = '-4px';
        arrowLeft = '50%';
        break;
      case 'start':
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.left - tooltipPos.width - 5;
        arrowTop = '50%';
        arrowLeft = 'calc(100% + 4px)';
        break;
      case 'end':
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.right + 5;
        arrowTop = '50%';
        arrowLeft = '-5px';
        break;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);

    this.renderer.setStyle(this.tooltipArrow, 'top', arrowTop);
    this.renderer.setStyle(this.tooltipArrow, 'left', arrowLeft);
    this.renderer.setStyle(this.tooltipArrow, 'transform', 'translate(-50%, -50%)');
  }

  private hideTooltip(): void {
    this.stopFollowing?.();
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
  }

  ngOnDestroy(): void {
    this.stopFollowing?.();
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
    }
  }
}
